export type DataCacheKey = (typeof DATA_CACHE_KEYS)[number];

/** Expansion state, keyed by prefix + a runtime id. Built by the `*CacheKey` helpers in `ui/`. */
export type UiStateCacheKey =
  | `cardDetailsExpanded_${string}`
  | `chartGroupExpanded_${string}`
  | `projectGroupExpanded_${string}`
  | `reportGroupExpanded_${string}`;

export type WorkbookCacheKey = DataCacheKey | UiStateCacheKey;

/** A retired key may be read, to migrate it, and deleted, but never written again. */
export type RetiredCacheKey = (typeof RETIRED_CACHE_KEYS)[number];
export type WritableCacheKey = Exclude<WorkbookCacheKey, RetiredCacheKey>;

/**
 * Keys no build writes any more. Never reuse a name. `lastProject` named the project, which the
 * `.metadata` report rows hold now. The other `last*` keys held copies of API data, now kept in
 * memory only and refetched after a reload. `usedSortFields` through `quantityTakeOffs` held pieces
 * of the processed estimate. `gcgrPhases`, `gcgrDetailGroups` and `finishKeys` were written for the
 * data-table reports. Each is deleted at startup, except the `LEGACY_CARD_CACHE_KEYS`.
 */
export const RETIRED_CACHE_KEYS = [
  "lastProject",
  "lastEstimate",
  "lastBidGroups",
  "lastBidTabs",
  "lastGCGRs",
  "usedSortFields",
  "sortFieldColumnNameMap",
  "labelSortFieldColumnNameMap",
  "overlineQuantitiesMap",
  "floorGroups",
  "quantityTakeOffs",
  "gcgrPhases",
  "gcgrDetailGroups",
  "finishKeys",
] as const;

export const LEGACY_CARD_CACHE_KEYS = ["lastEstimate", "lastBidGroups", "lastBidTabs"] as const;

/**
 * Every key the workbook cache is addressed by.
 *
 * These are identities: written under one name and read back under another means the read silently
 * returns `undefined` — no error, just a cold cache or a lost selection. `workbookCache.get`, `.set`
 * and `.delete` take this union rather than `string`, so a typo is a compile error.
 *
 * The space is closed in two halves. `DATA_CACHE_KEYS` are fixed names for what the workbook holds.
 * `UiStateCacheKey` is the open half — per-accordion expansion state, whose keys are built from a
 * prefix plus a runtime id. Adding a new prefixed family means adding it to that union.
 */
export const DATA_CACHE_KEYS = [
  ...RETIRED_CACHE_KEYS,
  "showDataSheets",
  "dataSheetsInitialized",
  "useEdiphiColors",
  "ediphiDatasourceOrder",
  "takeOffSourceOrder",
  "estimateCard",
  "gcgrCard",
  "contractingCard",
  "costModelCard",
  "togalProjectId",
  "togalSetId",
  "atoHubId",
  "atoHubName",
  "atoProjectId",
  "atoProjectName",
  "atoPackageId",
  "atoPackageName",
  "printLayout",
] as const;
